import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import { createStorageByUserId } from '@/services/storage-manager';
import { PERIODIC_MESSAGES_STORAGE_KEY } from '@/utils/constants';
import { generateSessionKeyFromMessage, type SessionKey } from '@/utils/session';

export interface PeriodicMessageStoreState {
  isHydrated: boolean;

  burnableMessages: Record<SessionKey, Record<string, ConversationMessageTypeUnion>>;

  deletedMessageIds: Record<SessionKey, number[]>;

  purgedMessageIds: Record<string, number>;
}

interface PeriodicMessageStoreActions {
  addBurnableMessage(message: ConversationMessageTypeUnion): void;

  removeBurnableMessageId(sessionKey: SessionKey, messageId: number): void;

  addDeletedMessageId(sessionKey: SessionKey, messageId: number): void;

  removeDeletedMessageId(sessionKey: SessionKey, messageId: number): void;

  setPurgedMessageId(key: string, messageId: number | null): void;

  clear(): void;

  setHydrated: () => void;
}

type PeriodicMessageStore = PeriodicMessageStoreState & PeriodicMessageStoreActions;

export const usePeriodicMessageStore = create<PeriodicMessageStore>()(
  persist(
    immer((set) => ({
      isHydrated: false as boolean,
      burnableMessages: {} as Record<SessionKey, Record<string, ConversationMessageTypeUnion>>,
      deletedMessageIds: {} as Record<SessionKey, number[]>,
      purgedMessageIds: {} as Record<string, number>,

      addBurnableMessage(message) {
        if (!message.ReadDelete || !message.AutoDeleteTime) return;
        const key = generateSessionKeyFromMessage(message);
        set((state) => {
          state.burnableMessages[key] ??= {};
          state.burnableMessages[key][message.ID] = message;
        });
      },

      removeBurnableMessageId(sessionKey, messageId) {
        set((state) => {
          const burnableMessages = state.burnableMessages?.[sessionKey];

          if (!burnableMessages) return;

          delete burnableMessages[messageId];

          if (Object.keys(burnableMessages).length === 0) {
            delete state.burnableMessages[sessionKey];
          }
        });
      },

      addDeletedMessageId(sessionKey, messageId) {
        set((state) => {
          state.deletedMessageIds[sessionKey] ??= [];
          if (!state.deletedMessageIds[sessionKey].includes(messageId)) {
            state.deletedMessageIds[sessionKey].push(messageId);
          }
        });
      },

      removeDeletedMessageId(sessionKey, messageId) {
        set((state) => {
          const deletedMessages = state.deletedMessageIds?.[sessionKey];

          if (!deletedMessages) return;

          state.deletedMessageIds[sessionKey] = deletedMessages.filter((id) => id !== messageId);

          if (state.deletedMessageIds[sessionKey].length === 0) {
            delete state.deletedMessageIds[sessionKey];
          }
        });
      },

      setPurgedMessageId(key, messageId) {
        set((state) => {
          if (messageId === null) {
            delete state.purgedMessageIds[key];
          } else if ((state.purgedMessageIds[key] ?? 0) < messageId) {
            state.purgedMessageIds[key] = messageId;
          }
        });
      },

      clear() {
        set({
          isHydrated: false,
          burnableMessages: {},
          deletedMessageIds: {},
          purgedMessageIds: {},
        });
      },

      setHydrated() {
        set({ isHydrated: true });
      },
    })),
    {
      name: PERIODIC_MESSAGES_STORAGE_KEY,
      version: 5,
      storage: createStorageByUserId('INDEXEDDB'),
      partialize: (state) => ({
        burnableMessages: state.burnableMessages,
        deletedMessageIds: state.deletedMessageIds,
        purgedMessageIds: state.purgedMessageIds,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
      migrate: () => ({
        burnableMessages: {},
        deletedMessageIds: {},
        purgedMessageIds: {},
      }),
    },
  ),
);
